import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../../config';
import { toast } from 'sonner';

import {
  ArrowSquareOut,
  ArrowsClockwise,
  CheckCircle,
  Clock,
  Copy,
  CreditCard,
  CurrencyDollar,
  DotsThreeVertical,
  Eye,
  FileText,
  Funnel,
  MagnifyingGlass,
  PencilSimple,
  Plus,
  TrendUp,
  UsersThree,
  WarningCircle,
  XCircle
} from '@phosphor-icons/react';
// Helper function for authenticated admin fetch
const adminFetch = async (url, options = {}) => {
  const token = localStorage.getItem('admin_token');
  if (!token) {
    toast.error('Not authenticated');
    throw new Error('Not authenticated');
  }
  
  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });
};

const AdminAffiliates = () => {
  // State
  const [affiliates, setAffiliates] = useState([]);
  const [globalStats, setGlobalStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [selectedEntries, setSelectedEntries] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    commission_type: 'percentage',
    commission_rate: 10,  // 10% default lifetime commission
    affiliate_code: '',
    notes: ''
  });

  // Payout form
  const [payoutData, setPayoutData] = useState({
    payment_reference: '',
    payment_method: 'bank',
    notes: ''
  });

  // Fetch affiliates
  const fetchAffiliates = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await adminFetch(`${BACKEND_URL}/api/admin/affiliates?${params}`);
      
      if (response.ok) {
        const data = await response.json();
        setAffiliates(data.affiliates || []);
      } else if (response.status === 401) {
        toast.error('Not authenticated');
      }
    } catch (error) {
      console.error('Error fetching affiliates:', error);
      if (error.message !== 'Not authenticated') {
        toast.error('Failed to load affiliates');
      }
    }
  }, [statusFilter]);

  // Fetch global stats
  const fetchGlobalStats = async () => {
    try {
      const response = await adminFetch(`${BACKEND_URL}/api/admin/affiliates/stats`);
      
      if (response.ok) {
        const data = await response.json();
        setGlobalStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchAffiliates(), fetchGlobalStats()]);
      setLoading(false);
    };
    loadData();
  }, [fetchAffiliates]);

  // Create affiliate
  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const response = await adminFetch(`${BACKEND_URL}/api/admin/affiliates`, {
        method: 'POST',
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Affiliate created with code: ${data.affiliate?.affiliate_code}`);
        setShowCreateModal(false);
        setFormData({ name: '', email: '', commission_type: 'percentage', commission_rate: 10, affiliate_code: '', notes: '' });
        fetchAffiliates();
        fetchGlobalStats();
      } else {
        toast.error(data.detail || 'Failed to create affiliate');
      }
    } catch (error) {
      toast.error('Failed to create affiliate');
    }
  };

  // Update affiliate
  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const response = await adminFetch(`${BACKEND_URL}/api/admin/affiliates/${selectedAffiliate.id}`, {
        method: 'PUT',
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success('Affiliate updated');
        setShowEditModal(false);
        fetchAffiliates();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to update affiliate');
      }
    } catch (error) {
      toast.error('Failed to update affiliate');
    }
  };

  // Activate/Suspend affiliate
  const toggleAffiliateStatus = async (affiliate, action) => {
    try {
      const response = await adminFetch(`${BACKEND_URL}/api/admin/affiliates/${affiliate.id}/${action}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        toast.success(`Affiliate ${action}d successfully`);
        fetchAffiliates();
        fetchGlobalStats();
      } else {
        const data = await response.json();
        toast.error(data.detail || `Failed to ${action} affiliate`);
      }
    } catch (error) {
      toast.error(`Failed to ${action} affiliate`);
    }
  };

  // Fetch ledger entries
  const fetchLedgerEntries = async (affiliateId) => {
    try {
      const response = await adminFetch(`${BACKEND_URL}/api/admin/affiliates/${affiliateId}/ledger`);
      
      if (response.ok) {
        const data = await response.json();
        setLedgerEntries(data.entries || []);
      }
    } catch (error) {
      console.error('Error fetching ledger:', error);
    }
  };

  // Mark entries as paid
  const handleMarkPaid = async (e) => {
    e.preventDefault();
    if (selectedEntries.length === 0) {
      toast.error('Please select entries to mark as paid');
      return;
    }
    
    try {
      const response = await adminFetch(`${BACKEND_URL}/api/admin/affiliates/ledger/mark-paid`, {
        method: 'POST',
        body: JSON.stringify({
          entry_ids: selectedEntries,
          ...payoutData
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Marked ${data.paid_count} entries as paid ($${data.total_amount})`);
        setShowPayoutModal(false);
        setSelectedEntries([]);
        setPayoutData({ payment_reference: '', payment_method: 'bank', notes: '' });
        if (selectedAffiliate) {
          fetchLedgerEntries(selectedAffiliate.id);
        }
        fetchAffiliates();
        fetchGlobalStats();
      } else {
        toast.error(data.detail || 'Failed to mark entries as paid');
      }
    } catch (error) {
      toast.error('Failed to process payout');
    }
  };

  // Copy referral URL
  const copyReferralUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Referral URL copied to clipboard');
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    const styles = {
      active: 'bg-green-500/20 text-green-400 border-green-500/30',
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      suspended: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    
    const icons = {
      active: <CheckCircle size={12} />,
      pending: <Clock size={12} />,
      suspended: <XCircle size={12} />
    };
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded border ${styles[status] || styles.pending}`}>
        {icons[status]}
        {status}
      </span>
    );
  };

  // Filtered affiliates
  const filteredAffiliates = affiliates.filter(a => 
    !searchQuery || 
    a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.affiliate_code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold">Affiliate Program</h1>
          <p className="text-gray-400 text-sm mt-1">Manage affiliates, track referrals, and process payouts</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-gradient-to-r from-[#6366F1] to-[#EC4899] rounded-lg flex items-center gap-2 hover:opacity-90 transition-opacity"
        >
          <Plus size={18} />
          Add Affiliate
        </button>
      </div>

      {/* Stats Cards */}
      {globalStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="glass rounded-xl p-4 border border-[rgba(255,255,255,0.1)]">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <UsersThree size={20} className="text-blue-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Total Affiliates</p>
                <p className="text-xl font-semibold">{globalStats.total_affiliates}</p>
                <p className="text-green-400 text-xs">{globalStats.active_affiliates} active</p>
              </div>
            </div>
          </div>
          
          <div className="glass rounded-xl p-4 border border-[rgba(255,255,255,0.1)]">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/20">
                <TrendUp size={20} className="text-purple-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Affiliate Revenue</p>
                <p className="text-xl font-semibold">${globalStats.total_affiliate_revenue?.toFixed(2) || '0.00'}</p>
                <p className="text-gray-500 text-xs">{globalStats.total_attributions} referrals</p>
              </div>
            </div>
          </div>
          
          <div className="glass rounded-xl p-4 border border-[rgba(255,255,255,0.1)]">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-500/20">
                <Clock size={20} className="text-yellow-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Pending Payouts</p>
                <p className="text-xl font-semibold">${globalStats.commission_pending?.toFixed(2) || '0.00'}</p>
                <p className="text-yellow-400 text-xs">Awaiting payment</p>
              </div>
            </div>
          </div>
          
          <div className="glass rounded-xl p-4 border border-[rgba(255,255,255,0.1)]">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/20">
                <CurrencyDollar size={20} className="text-green-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Total Paid</p>
                <p className="text-xl font-semibold">${globalStats.commission_paid?.toFixed(2) || '0.00'}</p>
                <p className="text-green-400 text-xs">Commission paid out</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, or code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#6366F1]"
          />
        </div>
        
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white focus:outline-none focus:border-[#6366F1]"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="suspended">Suspended</option>
        </select>
        
        <button
          onClick={() => { fetchAffiliates(); fetchGlobalStats(); }}
          className="px-4 py-2 glass rounded-lg flex items-center gap-2 hover:bg-[rgba(255,255,255,0.1)]"
        >
          <ArrowsClockwise size={16} />
          ArrowsClockwise
        </button>
      </div>

      {/* Affiliates Table */}
      <div className="glass rounded-xl border border-[rgba(255,255,255,0.1)] overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400">Loading affiliates...</div>
        ) : filteredAffiliates.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <UsersThree size={48} className="mx-auto mb-4 opacity-50" />
            <p>No affiliates found</p>
            <p className="text-sm mt-2">Create your first affiliate to get started</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-[rgba(255,255,255,0.05)]">
              <tr>
                <th className="text-left px-4 py-3 text-gray-400 font-medium text-sm">Affiliate</th>
                <th className="text-left px-4 py-3 text-gray-400 font-medium text-sm">Code</th>
                <th className="text-left px-4 py-3 text-gray-400 font-medium text-sm">Commission</th>
                <th className="text-left px-4 py-3 text-gray-400 font-medium text-sm">Stats</th>
                <th className="text-left px-4 py-3 text-gray-400 font-medium text-sm">Status</th>
                <th className="text-right px-4 py-3 text-gray-400 font-medium text-sm">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgba(255,255,255,0.05)]">
              {filteredAffiliates.map((affiliate) => (
                <tr key={affiliate.id} className="hover:bg-[rgba(255,255,255,0.02)]">
                  <td className="px-4 py-4">
                    <div>
                      <p className="font-medium">{affiliate.name}</p>
                      <p className="text-sm text-gray-400">{affiliate.email}</p>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <code className="px-2 py-1 bg-[rgba(255,255,255,0.1)] rounded text-sm font-mono">
                        {affiliate.affiliate_code}
                      </code>
                      <button
                        onClick={() => copyReferralUrl(affiliate.referral_url)}
                        className="p-1 hover:bg-[rgba(255,255,255,0.1)] rounded"
                        title="Copy referral URL"
                      >
                        <Copy size={14} className="text-gray-400" />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <p className="text-sm">
                      {affiliate.commission_rate}% {affiliate.commission_type === 'fixed' && '(fixed)'}
                    </p>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm">
                      <p>{affiliate.total_signups} signups</p>
                      <p className="text-gray-400">${affiliate.total_revenue?.toFixed(2) || '0.00'} revenue</p>
                      <p className="text-yellow-400">${affiliate.commission_pending?.toFixed(2) || '0.00'} pending</p>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <StatusBadge status={affiliate.status} />
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => {
                          setSelectedAffiliate(affiliate);
                          fetchLedgerEntries(affiliate.id);
                          setShowLedgerModal(true);
                        }}
                        className="p-2 hover:bg-[rgba(255,255,255,0.1)] rounded"
                        title="View ledger"
                      >
                        <FileText size={16} className="text-gray-400" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedAffiliate(affiliate);
                          setFormData({
                            name: affiliate.name,
                            email: affiliate.email,
                            commission_type: affiliate.commission_type,
                            commission_rate: affiliate.commission_rate,
                            affiliate_code: affiliate.affiliate_code,
                            notes: affiliate.notes || ''
                          });
                          setShowEditModal(true);
                        }}
                        className="p-2 hover:bg-[rgba(255,255,255,0.1)] rounded"
                        title="Edit affiliate"
                      >
                        <PencilSimple size={16} className="text-gray-400" />
                      </button>
                      {affiliate.status === 'pending' && (
                        <button
                          onClick={() => toggleAffiliateStatus(affiliate, 'activate')}
                          className="px-3 py-1 text-xs bg-green-500/20 text-green-400 rounded hover:bg-green-500/30"
                        >
                          Activate
                        </button>
                      )}
                      {affiliate.status === 'active' && (
                        <button
                          onClick={() => toggleAffiliateStatus(affiliate, 'suspend')}
                          className="px-3 py-1 text-xs bg-red-500/20 text-red-400 rounded hover:bg-red-500/30"
                        >
                          Suspend
                        </button>
                      )}
                      {affiliate.status === 'suspended' && (
                        <button
                          onClick={() => toggleAffiliateStatus(affiliate, 'activate')}
                          className="px-3 py-1 text-xs bg-green-500/20 text-green-400 rounded hover:bg-green-500/30"
                        >
                          Reactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Affiliate Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="glass rounded-xl border border-[rgba(255,255,255,0.1)] w-full max-w-md">
            <div className="p-6 border-b border-[rgba(255,255,255,0.1)]">
              <h2 className="text-xl font-semibold">Add New Affiliate</h2>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white focus:outline-none focus:border-[#6366F1]"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white focus:outline-none focus:border-[#6366F1]"
                  placeholder="john@example.com"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Commission Type</label>
                  <select
                    value={formData.commission_type}
                    onChange={(e) => setFormData({ ...formData, commission_type: e.target.value })}
                    className="w-full px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white focus:outline-none focus:border-[#6366F1]"
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Rate {formData.commission_type === 'percentage' ? '(%)' : '($)'}</label>
                  <input
                    type="number"
                    min="0"
                    max={formData.commission_type === 'percentage' ? 100 : 10000}
                    step="0.01"
                    value={formData.commission_rate}
                    onChange={(e) => setFormData({ ...formData, commission_rate: parseFloat(e.target.value) })}
                    className="w-full px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white focus:outline-none focus:border-[#6366F1]"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Custom Code (optional)</label>
                <input
                  type="text"
                  value={formData.affiliate_code}
                  onChange={(e) => setFormData({ ...formData, affiliate_code: e.target.value.toUpperCase() })}
                  className="w-full px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white focus:outline-none focus:border-[#6366F1] font-mono"
                  placeholder="ORIONJOE (auto-generated if empty)"
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty to auto-generate (e.g., MNM-ABC123)</p>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white focus:outline-none focus:border-[#6366F1]"
                  placeholder="Internal notes about this affiliate..."
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 glass rounded-lg hover:bg-[rgba(255,255,255,0.1)]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-[#6366F1] to-[#EC4899] rounded-lg hover:opacity-90"
                >
                  Create Affiliate
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* PencilSimple Affiliate Modal */}
      {showEditModal && selectedAffiliate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="glass rounded-xl border border-[rgba(255,255,255,0.1)] w-full max-w-md">
            <div className="p-6 border-b border-[rgba(255,255,255,0.1)]">
              <h2 className="text-xl font-semibold">Edit Affiliate</h2>
            </div>
            <form onSubmit={handleUpdate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white focus:outline-none focus:border-[#6366F1]"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white focus:outline-none focus:border-[#6366F1]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Commission Type</label>
                  <select
                    value={formData.commission_type}
                    onChange={(e) => setFormData({ ...formData, commission_type: e.target.value })}
                    className="w-full px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white focus:outline-none focus:border-[#6366F1]"
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Rate</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.commission_rate}
                    onChange={(e) => setFormData({ ...formData, commission_rate: parseFloat(e.target.value) })}
                    className="w-full px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white focus:outline-none focus:border-[#6366F1]"
                  />
                </div>
              </div>
              {!selectedAffiliate.code_used && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Affiliate Code</label>
                  <input
                    type="text"
                    value={formData.affiliate_code}
                    onChange={(e) => setFormData({ ...formData, affiliate_code: e.target.value.toUpperCase() })}
                    className="w-full px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white focus:outline-none focus:border-[#6366F1] font-mono"
                  />
                </div>
              )}
              {selectedAffiliate.code_used && (
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-sm text-yellow-400">
                  <WarningCircle size={14} className="inline mr-2" />
                  Code cannot be changed - already used for referrals
                </div>
              )}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white focus:outline-none focus:border-[#6366F1]"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 px-4 py-2 glass rounded-lg hover:bg-[rgba(255,255,255,0.1)]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-[#6366F1] to-[#EC4899] rounded-lg hover:opacity-90"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Ledger Modal */}
      {showLedgerModal && selectedAffiliate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="glass rounded-xl border border-[rgba(255,255,255,0.1)] w-full max-w-4xl max-h-[80vh] flex flex-col">
            <div className="p-6 border-b border-[rgba(255,255,255,0.1)] flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">Commission Ledger</h2>
                <p className="text-sm text-gray-400">{selectedAffiliate.name} ({selectedAffiliate.affiliate_code})</p>
              </div>
              {selectedEntries.length > 0 && (
                <button
                  onClick={() => setShowPayoutModal(true)}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg flex items-center gap-2 hover:bg-green-600"
                >
                  <CreditCard size={16} />
                  Mark {selectedEntries.length} as Paid
                </button>
              )}
            </div>
            <div className="flex-1 overflow-auto">
              {ledgerEntries.length === 0 ? (
                <div className="p-8 text-center text-gray-400">
                  <FileText size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No ledger entries yet</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-[rgba(255,255,255,0.05)] sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedEntries.length === ledgerEntries.filter(e => e.status !== 'paid' && e.status !== 'void').length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedEntries(ledgerEntries.filter(e => e.status !== 'paid' && e.status !== 'void').map(e => e.id));
                            } else {
                              setSelectedEntries([]);
                            }
                          }}
                          className="w-4 h-4 rounded"
                        />
                      </th>
                      <th className="text-left px-4 py-3 text-gray-400 font-medium text-sm">Date</th>
                      <th className="text-left px-4 py-3 text-gray-400 font-medium text-sm">Customer</th>
                      <th className="text-left px-4 py-3 text-gray-400 font-medium text-sm">Amount</th>
                      <th className="text-left px-4 py-3 text-gray-400 font-medium text-sm">Commission</th>
                      <th className="text-left px-4 py-3 text-gray-400 font-medium text-sm">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgba(255,255,255,0.05)]">
                    {ledgerEntries.map((entry) => (
                      <tr key={entry.id} className="hover:bg-[rgba(255,255,255,0.02)]">
                        <td className="px-4 py-3">
                          {entry.status !== 'paid' && entry.status !== 'void' && (
                            <input
                              type="checkbox"
                              checked={selectedEntries.includes(entry.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedEntries([...selectedEntries, entry.id]);
                                } else {
                                  setSelectedEntries(selectedEntries.filter(id => id !== entry.id));
                                }
                              }}
                              className="w-4 h-4 rounded"
                            />
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {new Date(entry.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm">{entry.user_email || 'Unknown'}</p>
                          <p className="text-xs text-gray-500">{entry.plan_type} - {entry.billing_period}</p>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          ${entry.gross_amount?.toFixed(2)}
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-green-400">
                            ${entry.commission_amount?.toFixed(2)}
                          </p>
                          <p className="text-xs text-gray-500">{entry.commission_rate}%</p>
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={entry.status} />
                          {entry.paid_at && (
                            <p className="text-xs text-gray-500 mt-1">
                              Paid: {new Date(entry.paid_at).toLocaleDateString()}
                            </p>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="p-4 border-t border-[rgba(255,255,255,0.1)]">
              <button
                onClick={() => {
                  setShowLedgerModal(false);
                  setSelectedEntries([]);
                }}
                className="px-4 py-2 glass rounded-lg hover:bg-[rgba(255,255,255,0.1)]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payout Modal */}
      {showPayoutModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="glass rounded-xl border border-[rgba(255,255,255,0.1)] w-full max-w-md">
            <div className="p-6 border-b border-[rgba(255,255,255,0.1)]">
              <h2 className="text-xl font-semibold">Record Payout</h2>
              <p className="text-sm text-gray-400 mt-1">
                Mark {selectedEntries.length} entries as paid
              </p>
            </div>
            <form onSubmit={handleMarkPaid} className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Payment Reference *</label>
                <input
                  type="text"
                  required
                  value={payoutData.payment_reference}
                  onChange={(e) => setPayoutData({ ...payoutData, payment_reference: e.target.value })}
                  className="w-full px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white focus:outline-none focus:border-[#6366F1]"
                  placeholder="e.g., WISE-123456 or BANK-REF-789"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Payment Method</label>
                <select
                  value={payoutData.payment_method}
                  onChange={(e) => setPayoutData({ ...payoutData, payment_method: e.target.value })}
                  className="w-full px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white focus:outline-none focus:border-[#6366F1]"
                >
                  <option value="bank">Bank Transfer</option>
                  <option value="wise">Wise</option>
                  <option value="paypal">PayPal</option>
                  <option value="stripe_transfer">Stripe Transfer</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Notes</label>
                <textarea
                  value={payoutData.notes}
                  onChange={(e) => setPayoutData({ ...payoutData, notes: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white focus:outline-none focus:border-[#6366F1]"
                  placeholder="Optional notes about this payout..."
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowPayoutModal(false)}
                  className="flex-1 px-4 py-2 glass rounded-lg hover:bg-[rgba(255,255,255,0.1)]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                >
                  Confirm Payout
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAffiliates;
